/**
 * API Client Wrapper
 * 
 * This file provides a clean interface to the auto-generated API client.
 * It handles:
 * - JWT authentication
 * - Base URL configuration
 * - Token management
 * - Singleton instance
 * 
 * Usage:
 *   import { apiClient } from './api/client';
 *   const response = await apiClient.chat.chatLastMessagesRetrieve();
 */

import { Configuration } from '../api-client/configuration';
import { ChatApi, DictionaryApi, TextToSpeechApi, UsersApi, WordExplorerApi } from '../api-client/api';

/**
 * API Client class that wraps the generated API clients
 */
class ApiClient {
  private config: Configuration;
  
  // API instances
  public chat: ChatApi;
  public dictionary: DictionaryApi;
  public textToSpeech: TextToSpeechApi;
  public users: UsersApi;
  public wordExplorer: WordExplorerApi;

  constructor() {
    this.config = this.createConfig();
    this.chat = new ChatApi(this.config);
    this.dictionary = new DictionaryApi(this.config);
    this.textToSpeech = new TextToSpeechApi(this.config);
    this.users = new UsersApi(this.config);
    this.wordExplorer = new WordExplorerApi(this.config);
  }

  /**
   * Creates configuration with current auth token and base URL
   */
  private createConfig(): Configuration {
    const basePath = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    
    return new Configuration({
      basePath,
      // accessToken can be a string or a function that returns a string
      accessToken: () => {
        const token = localStorage.getItem('accessToken');
        return token || '';
      }
    });
  }

  /**
   * Updates the authentication token and recreates API instances
   * Call this after login/signup
   */
  public updateToken(accessToken: string, refreshToken?: string): void {
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    
    // Recreate config and API instances with new token
    this.config = this.createConfig();
    this.chat = new ChatApi(this.config);
    this.dictionary = new DictionaryApi(this.config);
    this.textToSpeech = new TextToSpeechApi(this.config);
    this.users = new UsersApi(this.config);
    this.wordExplorer = new WordExplorerApi(this.config);
  }

  /**
   * Clears authentication and recreates API instances
   * Call this on logout
   */
  public clearAuth(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    
    // Recreate config and API instances without token
    this.config = this.createConfig();
    this.chat = new ChatApi(this.config);
    this.dictionary = new DictionaryApi(this.config);
    this.textToSpeech = new TextToSpeechApi(this.config);
    this.users = new UsersApi(this.config);
    this.wordExplorer = new WordExplorerApi(this.config);
  }

  /**
   * Gets the current access token
   */
  public getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  /**
   * Gets the current refresh token
   */
  public getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  /**
   * Checks if user is authenticated
   */
  public isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export types for use in components
export type { 
  ConversationRequestRequest,
  ConversationResponse,
  MessageInputRequest,
  MessageOutput,
  LastMessagesResponse,
  Language,
  TokenRefreshRequest,
  TokenRefresh,
  TokenObtainPairRequest,
  TokenObtainPair,
  UserCreateRequest,
  UserCreateResponse,
  UserOptionsResponse,
  UserProfileResponse,
  UserMarkedWord,
  UserMarkedWordRequest,
  PatchedUserMarkedWordRequest
} from '../api-client/api';
